import React, { useState, useEffect } from 'react';
import { 
  Bell,
  LogIn,
  LogOut,
  AlertOctagon,
  Circle,
  Activity,
  PlayCircle,
  XCircle,
  BrainCircuit,
  Wifi,
  Database
} from 'lucide-react';
import { useAuth } from '../AuthContext';
import { decisionsApi } from '../lib/api';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Skeleton } from './ui/skeleton';

interface Notification {
  id: string;
  source: string;
  event_type: string;
  severity: string;
  payload: any;
  timestamp: string;
}

const NotificationCenter: React.FC = () => {
  const { currentUser } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!currentUser) return;

    const fetchNotifications = async () => {
      try {
        const data = await decisionsApi.list({ limit: 10 }) as any;
        const events = (data.decisions || [])
          .filter((d: any) => ['signal', 'execution', 'exit', 'rejection'].includes(d.event_type))
          .map((d: any) => ({
            id: d.id,
            source: d.pattern_name || d.symbol || 'system',
            event_type: d.event_type,
            severity: d.event_type === 'exit' ? (d.realized_pnl >= 0 ? 'info' : 'warning') : 'info',
            payload: d,
            timestamp: d.timestamp,
          }));
        setNotifications(events);
        setUnreadCount(Math.min(events.length, 3));
      } catch { /* non-fatal */ } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
    
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [currentUser]);

  const handleOpen = () => {
    setOpen(true);
    setUnreadCount(0); // clear unread on open
  };

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'system_startup': return <PlayCircle className="text-green-500 w-4 h-4" />;
      case 'system_shutdown': return <StopCircle className="text-gray-500 w-4 h-4" />;
      case 'user_login': return <LogIn className="text-blue-500 w-4 h-4" />;
      case 'user_logout': return <LogOut className="text-gray-400 w-4 h-4" />;
      case 'circuit_breaker': return <AlertOctagon className="text-red-500 w-4 h-4" />;
      case 'health_check': return <Activity className="text-teal-500 w-4 h-4" />;
      case 'market_regime_change': return <Globe className="text-indigo-500 w-4 h-4" />;
      case 'risk_limit_breach': return <AlertTriangle className="text-red-600 w-4 h-4" />;
      default:
        if (type.startsWith('db_')) return <Database className="text-blue-400 w-4 h-4" />;
        if (type.startsWith('api_')) return <Wifi className="text-emerald-500 w-4 h-4" />;
        if (type.startsWith('model_')) return <BrainCircuit className="text-purple-500 w-4 h-4" />;
        if (type.startsWith('order_')) return <ListTodo className="text-amber-500 w-4 h-4" />;
        return <Circle className="text-gray-400 w-4 h-4" />;
    }
  };

  // Safe fallback for icons not explicitly imported above
  const StopCircle = ({ className }: {className?: string}) => <XCircle className={className} />;
  const Globe = ({ className }: {className?: string}) => <Circle className={className} />;
  const AlertTriangle = ({ className }: {className?: string}) => <AlertOctagon className={className} />;
  const ListTodo = ({ className }: {className?: string}) => <Activity className={className} />;

  const getEventLabel = (n: Notification) => {
    if (n.payload?.message) return n.payload.message;
    if (n.event_type === 'circuit_breaker') {
      return `Circuit breaker ${n.payload?.action || 'triggered'} for ${n.payload?.reason || 'unknown'}`;
    }
    return n.event_type.replace(/_/g, ' ');
  };

  const formatTime = (ts: string) => {
    try {
      const d = new Date(ts);
      // If today, just time. Else date
      const today = new Date();
      if (d.getDate() === today.getDate() && d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear()) {
        return d.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit' });
      }
      return d.toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return ts;
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" onClick={handleOpen} className="relative mt-1">
          <Bell className="w-5 h-5 text-muted-foreground" />
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 w-4 h-4 p-0 flex items-center justify-center text-[10px]"
            >
              {unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      
      <PopoverContent align="end" className="w-[340px] p-0" sideOffset={8}>
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h4 className="font-semibold text-sm">Notifications</h4>
          <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full">
            {notifications.length} recent
          </span>
        </div>
        
        <ScrollArea className="h-[300px]">
          {loading && notifications.length === 0 ? (
            <div className="p-4 space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="flex gap-3">
                  <Skeleton className="w-8 h-8 rounded-full flex-shrink-0" />
                  <div className="space-y-1.5 flex-1">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-3 w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              No recent notifications
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((n, i) => (
                <div 
                  key={n.id || i}
                  className="flex gap-3 px-4 py-3 hover:bg-muted/50 transition-colors cursor-default"
                >
                  <div className="mt-0.5 w-6 h-6 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                    {getEventIcon(n.event_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium leading-tight truncate">
                      {getEventLabel(n)}
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {formatTime(n.timestamp)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
        
        <div className="p-2 border-t bg-muted/30">
          <Button variant="ghost" className="w-full text-xs h-8 text-primary font-medium hover:text-primary hover:bg-primary/10">
            View All Activity
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationCenter;
